import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPackById } from "../../services/apiServices";
import {
  Button,
  Spinner,
  Alert,
  Table,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";

const ViewPack = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const getPack = async () => {
      try {
        setLoading(true);
        const response = await fetchPackById(id);

        if (response.status && response.data) {
          setPack(response.data);
        } else {
          setError("Failed to fetch pack details");
          toast.error("Failed to fetch pack details");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch pack details");
        toast.error("Failed to fetch pack details");
      } finally {
        setLoading(false);
      }
    };

    getPack();
  }, [id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const openItemModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const closeItemModal = () => {
    setSelectedItem(null);
    setModalOpen(false);
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading pack details...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{error}</Alert>
        </Container>
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">Pack not found</Alert>
        </Container>
      </div>
    );
  }

  // Calculate total value and item counts
  const totalValue =
    pack.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
  const uniqueItems = new Set(pack.items?.map((item) => item.name)).size;
  const itemCount = pack.items?.length || 0;

  // Group items by name for summary
  const itemSummary = {};
  pack.items?.forEach((item) => {
    if (!itemSummary[item.name]) {
      itemSummary[item.name] = {
        count: 0,
        totalValue: 0,
        amount: item.amount,
        image: item.image,
      };
    }
    itemSummary[item.name].count++;
    itemSummary[item.name].totalValue += item.amount;
  });

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="PackDraw" breadcrumbItem="Pack Details" />

        <Row className="mb-3">
          <Col>
            <h4 className="mb-0">Pack Details</h4>
          </Col>
          <Col className="text-end">
            <Button
              color="primary"
              onClick={() => navigate(`/packs/edit/${id}`)}
              className="me-2">
              <i className="ri-edit-line"></i> Edit Pack
            </Button>
            <Button color="secondary" onClick={() => navigate("/packs")}>
              <i className="ri-arrow-left-line"></i> Back to List
            </Button>
          </Col>
        </Row>

        {/* Pack Summary Cards */}
        <Row className="mb-4">
          <Col xl={3} lg={6}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-2">Pack Value</p>
                    <h4 className="mb-0">{formatCurrency(totalValue)}</h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                      <i className="ri-money-dollar-circle-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} lg={6}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-2">Total Items</p>
                    <h4 className="mb-0">{itemCount}</h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded-circle fs-4">
                      <i className="ri-list-check"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} lg={6}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-2">Unique Items</p>
                    <h4 className="mb-0">{uniqueItems}</h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded-circle fs-4">
                      <i className="ri-stack-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} lg={6}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-2">Pack Cost</p>
                    <h4 className="mb-0">{formatCurrency(pack.packAmount)}</h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-4">
                      <i className="ri-shopping-bag-line"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Pack Details */}
          <Col lg={6}>
            <Card className="shadow-sm border-0 mb-4">
              <CardHeader className="bg-primary text-white">
                <h5 className="mb-0">Pack Information</h5>
              </CardHeader>
              <CardBody>
                <Table bordered striped responsive>
                  <tbody>
                    <tr>
                      <th style={{ width: "40%" }}>Pack Name</th>
                      <td>
                        <strong>{pack.name}</strong>
                      </td>
                    </tr>
                    <tr>
                      <th>Pack ID</th>
                      <td className="font-monospace">{pack._id}</td>
                    </tr>
                    <tr>
                      <th>Creator</th>
                      <td>
                        <div>{pack.creator}</div>
                        <small className="text-muted">
                          ID: {pack.creatorId}
                        </small>
                      </td>
                    </tr>
                    <tr>
                      <th>Pack Amount</th>
                      <td>
                        <Badge color="primary" pill className="fs-6">
                          {pack.packAmount}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Outcome Multiplier</th>
                      <td>
                        <Badge
                          color={pack.outCome >= 1 ? "success" : "warning"}
                          pill
                          className="fs-6">
                          {pack.outCome}x
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Rare Multiplier</th>
                      <td>
                        <Badge color="info" pill className="fs-6">
                          {pack.rareMultiPlayer}x
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Epic Multiplier</th>
                      <td>
                        <Badge color="warning" pill className="fs-6">
                          {pack.epicMultiPlayer}x
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <Badge
                          color={pack.isApproved ? "success" : "danger"}
                          pill
                          className="fs-6">
                          {pack.isApproved ? "Approved" : "Pending Approval"}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Created</th>
                      <td>{formatDate(pack.createdAt)}</td>
                    </tr>
                    <tr>
                      <th>Updated</th>
                      <td>{formatDate(pack.updatedAt)}</td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          {/* Items Summary */}
          <Col lg={6}>
            <Card className="shadow-sm border-0 mb-4">
              <CardHeader className="bg-info text-white">
                <h5 className="mb-0">Items Summary</h5>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Unit Value</th>
                        <th className="text-end">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(itemSummary).map(
                        ([itemName, itemData]) => (
                          <tr key={itemName}>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={itemData.image}
                                  alt={itemName}
                                  className="rounded me-2"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    objectFit: "cover",
                                  }}
                                />
                                <span className="fw-medium">{itemName}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              <Badge color="primary" pill>
                                {itemData.count}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {formatCurrency(itemData.amount)}
                            </td>
                            <td className="text-end fw-bold text-success">
                              {formatCurrency(itemData.totalValue)}
                            </td>
                          </tr>
                        ),
                      )}
                      <tr className="table-active">
                        <td colSpan="3" className="text-end fw-bold">
                          Total Pack Value:
                        </td>
                        <td className="text-end fw-bold fs-5 text-success">
                          {formatCurrency(totalValue)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* All Items Table */}
        <Card className="shadow-sm border-0">
          <CardHeader className="bg-secondary text-white">
            <h5 className="mb-0">All Items ({itemCount} items)</h5>
          </CardHeader>
          <CardBody>
            <div className="table-responsive">
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item Name</th>
                    <th>Image</th>
                    <th className="text-end">Value</th>
                    <th>Created</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pack.items?.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td className="fw-medium">{item.name}</td>
                      <td>
                        <img
                          src={item.image}
                          alt={item.name}
                          className="rounded"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                        />
                      </td>
                      <td className="text-end fw-bold">
                        {formatCurrency(item.amount)}
                      </td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          color="info"
                          onClick={() => openItemModal(item)}
                          title="View Details">
                          <i className="ri-eye-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Item Detail Modal */}
        <Modal isOpen={modalOpen} toggle={closeItemModal} size="lg">
          <ModalHeader toggle={closeItemModal}>
            Item Details: {selectedItem?.name}
          </ModalHeader>
          <ModalBody>
            {selectedItem && (
              <Row>
                <Col md={4} className="text-center">
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.name}
                    className="img-fluid rounded mb-3"
                    style={{ maxHeight: "200px", objectFit: "contain" }}
                  />
                  <h4 className="fw-bold">
                    {formatCurrency(selectedItem.amount)}
                  </h4>
                </Col>
                <Col md={8}>
                  <Table bordered>
                    <tbody>
                      <tr>
                        <th style={{ width: "30%" }}>Item ID</th>
                        <td className="font-monospace">{selectedItem._id}</td>
                      </tr>
                      <tr>
                        <th>Name</th>
                        <td className="fw-bold">{selectedItem.name}</td>
                      </tr>
                      <tr>
                        <th>Value</th>
                        <td className="fw-bold text-success">
                          {formatCurrency(selectedItem.amount)}
                        </td>
                      </tr>
                      <tr>
                        <th>Created</th>
                        <td>{formatDate(selectedItem.createdAt)}</td>
                      </tr>
                      <tr>
                        <th>Updated</th>
                        <td>{formatDate(selectedItem.updatedAt)}</td>
                      </tr>
                    </tbody>
                  </Table>
                  <div className="mt-3">
                    <h6>Description:</h6>
                    <div className="border rounded p-3 bg-light">
                      {selectedItem.description || "No description available"}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeItemModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewPack;
